// src/component/common/Skeleton.jsx
//
// 재사용 가능한 스켈레톤 컴포넌트.
// 기본 베이스(`Skeleton`)와 페이지별 자주 쓰이는 레이아웃 변형을 제공한다.
//
// 사용 예)
//   <Skeleton className="h-4 w-32" />
//   <ServiceCardSkeleton />
//   <ServiceGridSkeleton count={6} />

import React from "react";

const PULSE_CLASS = "animate-pulse bg-bg-tertiary";

export default function Skeleton({ className = "", rounded = "rounded-md", style }) {
  return <div className={`${PULSE_CLASS} ${rounded} ${className}`} style={style} />;
}

export function SkeletonText({ width = "w-full", height = "h-3.5", className = "" }) {
  return <Skeleton className={`${height} ${width} ${className}`} />;
}

export function SkeletonCircle({ size = "h-10 w-10", className = "" }) {
  return <Skeleton className={`${size} ${className}`} rounded="rounded-full" />;
}

// ── Service / Bookmark / Search 그리드용 카드 ─────────────────────────────────
export function ServiceCardSkeleton() {
  return (
    <div className="rounded-2xl border border-border-tertiary bg-white overflow-hidden">
      <Skeleton className="w-full aspect-[3/2]" rounded="rounded-none" />
      <div className="p-4">
        <SkeletonText height="h-4" width="w-11/12" className="mb-2" />
        <SkeletonText height="h-3" width="w-2/3" className="mb-3" />
        <div className="flex gap-1 mb-3">
          <Skeleton className="h-5 w-14" rounded="rounded-full" />
          <Skeleton className="h-5 w-10" rounded="rounded-full" />
        </div>
        <div className="flex items-center justify-between">
          <SkeletonText height="h-3" width="w-16" />
          <SkeletonText height="h-4" width="w-20" />
        </div>
      </div>
    </div>
  );
}

export function ServiceGridSkeleton({ count = 6, className = "" }) {
  return (
    <div
      className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 ${className}`}
    >
      {Array.from({ length: count }).map((_, i) => (
        <ServiceCardSkeleton key={i} />
      ))}
    </div>
  );
}

// ── 가로 캐러셀 (홈, 서비스 상세 등) ──────────────────────────────────────────
export function CarouselSkeleton({ count = 4, title = true }) {
  return (
    <div className="mt-10">
      {title && <SkeletonText height="h-5" width="w-40" className="mb-3 ms-4" />}
      <div className="px-4 flex gap-3 overflow-hidden">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="w-44 shrink-0">
            <Skeleton className="w-full aspect-[3/2] mb-2" />
            <SkeletonText height="h-4" width="w-11/12" className="mb-1.5" />
            <SkeletonText height="h-3" width="w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 홈 화면 ─────────────────────────────────────────────────────────────────
export function HomeSkeleton() {
  return (
    <div className="mt-6">
      <Skeleton className="w-full aspect-[21/9]" rounded="rounded-2xl" />

      <div className="mt-8 grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <SkeletonCircle size="h-14 w-14" />
            <SkeletonText height="h-3" width="w-12" />
          </div>
        ))}
      </div>

      <CarouselSkeleton />
      <CarouselSkeleton />
    </div>
  );
}

// ── 카테고리 화면 ───────────────────────────────────────────────────────────
export function CategoryPageSkeleton() {
  return (
    <div className="mt-6">
      <Skeleton className="w-full aspect-[21/9]" rounded="rounded-2xl" />

      <div className="mt-8 grid grid-cols-4 sm:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <SkeletonCircle size="h-12 w-12" />
            <SkeletonText height="h-3" width="w-10" />
          </div>
        ))}
      </div>

      <div className="mt-8 px-4">
        <SkeletonText height="h-5" width="w-32" className="mb-4" />
        <ServiceGridSkeleton count={6} className="px-0" />
      </div>
    </div>
  );
}

// ── 서비스 상세 ─────────────────────────────────────────────────────────────
export function ServiceDetailSkeleton() {
  return (
    <div className="max-w-6xl mx-auto pb-12">
      <div className="mt-6 px-4">
        <Skeleton className="w-full aspect-[16/10]" rounded="rounded-2xl" />
        <div className="mt-4">
          <SkeletonText height="h-6" width="w-3/4" className="mb-3" />
          <div className="flex gap-2 mb-3">
            <Skeleton className="h-5 w-16" rounded="rounded-full" />
            <Skeleton className="h-5 w-20" rounded="rounded-full" />
          </div>
          <SkeletonText height="h-3" width="w-1/2" className="mb-2" />
          <SkeletonText height="h-3" width="w-1/3" />
        </div>

        <div className="mt-6 space-y-2">
          <SkeletonText height="h-3" width="w-full" />
          <SkeletonText height="h-3" width="w-11/12" />
          <SkeletonText height="h-3" width="w-10/12" />
          <SkeletonText height="h-3" width="w-9/12" />
        </div>

        <div className="mt-8">
          <SkeletonText height="h-5" width="w-32" className="mb-3" />
          <div className="space-y-2">
            <SkeletonText height="h-3" width="w-full" />
            <SkeletonText height="h-3" width="w-11/12" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 리스트형 화면 (커뮤니티 게시글) ─────────────────────────────────────────
export function ListItemSkeleton() {
  return (
    <div className="px-4 py-4 border-b border-gray-100">
      <div className="flex items-center gap-2 mb-2">
        <Skeleton className="h-4 w-16" rounded="rounded-full" />
        <SkeletonText height="h-3" width="w-20" />
      </div>
      <SkeletonText height="h-4" width="w-10/12" className="mb-2" />
      <SkeletonText height="h-3" width="w-7/12" className="mb-3" />
      <div className="flex items-center gap-4">
        <SkeletonText height="h-3" width="w-12" />
        <SkeletonText height="h-3" width="w-12" />
      </div>
    </div>
  );
}

export function ListSkeleton({ count = 6 }) {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <ListItemSkeleton key={i} />
      ))}
    </div>
  );
}

// ── 예약 카드 ───────────────────────────────────────────────────────────────
export function BookingCardSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <Skeleton className="w-full h-40" rounded="rounded-none" />
      <div className="p-4">
        <SkeletonText height="h-3" width="w-24" className="mb-2" />
        <SkeletonText height="h-4" width="w-11/12" className="mb-3" />
        <SkeletonText height="h-3" width="w-32" className="mb-3" />
        <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
          <SkeletonText height="h-3" width="w-16" />
          <SkeletonText height="h-4" width="w-20" />
        </div>
      </div>
    </div>
  );
}

export function BookingListSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <BookingCardSkeleton key={i} />
      ))}
    </div>
  );
}

// ── 예약 상세 ───────────────────────────────────────────────────────────────
export function BookingDetailSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-4 py-4">
        <SkeletonText height="h-5" width="w-40" />
      </div>
      <div className="px-4 py-4 max-w-3xl mx-auto">
        <Skeleton className="w-full h-48" rounded="rounded-2xl" />
        <div className="mt-4 bg-white rounded-2xl p-4 border border-gray-200">
          <SkeletonText height="h-4" width="w-2/3" className="mb-3" />
          <SkeletonText height="h-3" width="w-1/2" className="mb-2" />
          <SkeletonText height="h-3" width="w-1/3" />
        </div>
        <div className="mt-4 bg-white rounded-2xl p-4 border border-gray-200 space-y-3">
          <SkeletonText height="h-3" width="w-full" />
          <SkeletonText height="h-3" width="w-11/12" />
          <SkeletonText height="h-3" width="w-10/12" />
        </div>
      </div>
    </div>
  );
}

// ── 리뷰 카드 ───────────────────────────────────────────────────────────────
export function ReviewCardSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4">
      <div className="flex items-center gap-3 mb-3">
        <SkeletonCircle size="h-10 w-10" />
        <div className="flex-1">
          <SkeletonText height="h-3" width="w-24" className="mb-1" />
          <SkeletonText height="h-3" width="w-16" />
        </div>
      </div>
      <SkeletonText height="h-3" width="w-full" className="mb-1.5" />
      <SkeletonText height="h-3" width="w-11/12" className="mb-1.5" />
      <SkeletonText height="h-3" width="w-3/4" />
    </div>
  );
}

export function ReviewListSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <ReviewCardSkeleton key={i} />
      ))}
    </div>
  );
}

// ── 알림 ───────────────────────────────────────────────────────────────────
export function NotificationListSkeleton({ count = 6 }) {
  return (
    <div className="flex flex-col gap-1">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 px-4 py-4">
          <div className="w-2 mt-1" />
          <SkeletonCircle size="h-11 w-11" />
          <div className="flex-1 min-w-0">
            <SkeletonText height="h-4" width="w-10/12" className="mb-1.5" />
            <SkeletonText height="h-3" width="w-full" className="mb-1.5" />
            <SkeletonText height="h-3" width="w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── 쿠폰 ───────────────────────────────────────────────────────────────────
export function CouponCardSkeleton() {
  return (
    <div className="relative bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-bg-tertiary" />
      <div className="ps-5 pe-4 py-4">
        <div className="flex items-start justify-between mb-2">
          <SkeletonText height="h-5" width="w-20" />
          <Skeleton className="h-4 w-14" rounded="rounded-full" />
        </div>
        <SkeletonText height="h-4" width="w-10/12" className="mb-3" />
        <div className="my-3 border-t border-dashed border-gray-200" />
        <SkeletonText height="h-3" width="w-2/3" className="mb-1.5" />
        <SkeletonText height="h-3" width="w-1/2" />
      </div>
    </div>
  );
}

export function CouponListSkeleton({ count = 4 }) {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <CouponCardSkeleton key={i} />
      ))}
    </div>
  );
}

// ── 쿠폰 상세 ───────────────────────────────────────────────────────────────
export function CouponDetailSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-4 py-4">
        <SkeletonText height="h-5" width="w-32" />
      </div>
      <div className="px-4 py-6 max-w-xl mx-auto">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <SkeletonText height="h-6" width="w-24" className="mb-3" />
          <SkeletonText height="h-4" width="w-11/12" className="mb-2" />
          <SkeletonText height="h-4" width="w-9/12" className="mb-4" />
          <div className="border-t border-dashed border-gray-200 my-4" />
          <SkeletonText height="h-3" width="w-2/3" className="mb-2" />
          <SkeletonText height="h-3" width="w-1/2" className="mb-2" />
          <SkeletonText height="h-3" width="w-3/4" />
        </div>
      </div>
    </div>
  );
}

// ── 커뮤니티 상세 ───────────────────────────────────────────────────────────
export function CommunityDetailSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <div className="px-4 py-8 max-w-3xl mx-auto">
        <SkeletonText height="h-4" width="w-16" className="mb-6" />
        <Skeleton className="h-7 w-20" rounded="rounded-md" />
        <SkeletonText height="h-7" width="w-4/5" className="mt-4 mb-3" />
        <div className="flex items-center gap-2 mb-6">
          <SkeletonCircle size="h-8 w-8" />
          <SkeletonText height="h-3" width="w-24" />
        </div>
        <div className="space-y-2">
          <SkeletonText height="h-3" width="w-full" />
          <SkeletonText height="h-3" width="w-11/12" />
          <SkeletonText height="h-3" width="w-10/12" />
          <SkeletonText height="h-3" width="w-9/12" />
          <SkeletonText height="h-3" width="w-11/12" />
        </div>
      </div>
    </div>
  );
}

// ── 리뷰 상세 ───────────────────────────────────────────────────────────────
export function ReviewDetailSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-4 py-4">
        <SkeletonText height="h-5" width="w-32" />
      </div>
      <div className="px-4 py-6 max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <SkeletonCircle size="h-12 w-12" />
            <div className="flex-1">
              <SkeletonText height="h-3" width="w-24" className="mb-1" />
              <SkeletonText height="h-3" width="w-16" />
            </div>
          </div>
          <SkeletonText height="h-3" width="w-full" className="mb-1.5" />
          <SkeletonText height="h-3" width="w-11/12" className="mb-1.5" />
          <SkeletonText height="h-3" width="w-10/12" />
        </div>
      </div>
    </div>
  );
}

// ── 프로바이더 프로필 ─────────────────────────────────────────────────────
export function ProviderProfileSkeleton() {
  return (
    <div className="max-w-6xl mx-auto pb-12">
      <div className="mt-6 px-4">
        <Skeleton className="w-full h-40" rounded="rounded-2xl" />
        <div className="-mt-10 ms-4 mb-4">
          <SkeletonCircle size="h-20 w-20 border-4 border-white" />
        </div>
        <SkeletonText height="h-5" width="w-1/2" className="mb-2" />
        <SkeletonText height="h-3" width="w-2/3" className="mb-1" />
        <SkeletonText height="h-3" width="w-1/2" />

        <div className="mt-8">
          <SkeletonText height="h-5" width="w-32" className="mb-3" />
          <ServiceGridSkeleton count={6} className="px-0" />
        </div>
      </div>
    </div>
  );
}

// ── 게시글 작성/편집 폼 ─────────────────────────────────────────────────────
export function PostFormSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <div className="px-4 py-6 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <SkeletonText height="h-4" width="w-12" />
          <SkeletonText height="h-4" width="w-20" />
          <Skeleton className="h-9 w-20" rounded="rounded-lg" />
        </div>
        <SkeletonText height="h-3" width="w-16" className="mb-2" />
        <div className="flex gap-2 mb-6">
          <Skeleton className="h-8 w-16" rounded="rounded-full" />
          <Skeleton className="h-8 w-20" rounded="rounded-full" />
          <Skeleton className="h-8 w-14" rounded="rounded-full" />
        </div>
        <SkeletonText height="h-3" width="w-12" className="mb-2" />
        <Skeleton className="h-12 w-full mb-4" rounded="rounded-xl" />
        <SkeletonText height="h-3" width="w-16" className="mb-2" />
        <Skeleton className="h-48 w-full" rounded="rounded-xl" />
      </div>
    </div>
  );
}

// ── 페이지 단위 라우트 fallback (Suspense) ──────────────────────────────────
export function RouteFallbackSkeleton() {
  return (
    <div className="max-w-6xl mx-auto mt-6 px-4">
      <Skeleton className="w-full h-10 mb-4" />
      <Skeleton className="w-full aspect-[21/9] mb-6" rounded="rounded-2xl" />
      <ServiceGridSkeleton count={6} className="px-0" />
    </div>
  );
}
